import { exactInteger } from '@/lib/exact'
import type { DecideReason, FormBlock, FormLine } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import { actorsAreIdentical, secondPersonRefusal, submitWrite, writeRefusal } from './writes'

/**
 * The six writes on Access & Audit: roles, attestations and break-glass.
 *
 * # An elevation grants nothing, and every control here has to say so
 *
 * This is the misreading with the worst consequences on the console, because it
 * is the exact opposite of what "break-glass" means everywhere else:
 *
 * > *"Break-glass here is a RECORD, never a grant: no handler consults an
 * > elevation before a permission check, `Principal` has no elevated field…
 * > What an elevation does is attach its id to every audit row written while it
 * > is open, so the post-hoc review is a query rather than an archaeology of
 * > timestamps."*
 *
 * The usual reading — an elevation that CONFERS authority — would be a second
 * authorisation path with no permission matrix behind it and no 403 naming a
 * role. Somebody who requests one expecting new powers finds nothing has
 * changed, during an incident, under time pressure. So the request control
 * leads with what it does not do, and the confirmation says it again.
 *
 * # A request is not a session
 *
 * `POST /break-glass` answers 201 with a PENDING request. *"what exists now is
 * a request, and it confers nothing until a second admin acknowledges it."*
 * Rendering that as an open elevation would be law 2's optimistic-UI mistake
 * moved to a different kind of instruction.
 *
 * # Two more maker-checker rules, and they reuse the mechanism rather than
 * re-deriving it
 *
 * `ErrSelfAcknowledge` and `ErrSelfReview` both compare the caller against
 * `requested_by`, which carries the `operator:` prefix like every recorded
 * actor. `secondPersonRefusal` does that comparison once. A second derivation
 * here would be a second chance to compare bare subjects and fail OPEN, which
 * is the failure #149 was built to make impossible to repeat.
 *
 * Both answer **403** with `maker_is_checker`, where AML's answer 409. This
 * module does not assume either: the refusal is rendered from what came back.
 */

const PERM_ROLE_WRITE = 'staff.role.write'
const PERM_ATTEST = 'access_review.attest'
/**
 * `break_glass.use`, not `break_glass`.
 *
 * Checked against `internal/staff/staff.go` rather than inferred from the route
 * name. Getting it wrong fails CLOSED — `holdsPermission` returns false for a
 * key nobody grants, so every break-glass control would be permanently disabled
 * for everybody, with a refusal naming a role that does hold the real one. Safe,
 * and silently useless.
 */
const PERM_BREAK_GLASS = 'break_glass.use'

const ROLE_ADMIN = 'Admin'

export const STAFF_PATH = '/staff'
export const BREAK_GLASS_WRITE_PATH = '/break-glass'

export function staffRolesPath(subject: string): string {
  return `${STAFF_PATH}/${encodeURIComponent(subject)}/roles`
}

export function staffRoleRevokePath(subject: string, role: string): string {
  return `${staffRolesPath(subject)}/${encodeURIComponent(role)}/revoke`
}

export function staffAttestPath(subject: string): string {
  return `${STAFF_PATH}/${encodeURIComponent(subject)}/access-reviews`
}

export function elevationPath(id: number, verb: 'acknowledge' | 'review'): string {
  return `${BREAK_GLASS_WRITE_PATH}/${String(id)}/${verb}`
}

/** The seven staff roles, as the backend's closed set. */
const ROLES: readonly (readonly [string, string])[] = [
  ['support', 'support — reads the screens an agent works from'],
  ['ops', 'ops — the queue work, and setting an account restricted'],
  ['compliance', 'compliance — AML, sanctions, and account state'],
  ['finance', 'finance — pricing, billing and the ledger'],
  ['growth', 'growth — the referral programme and the catalogue'],
  ['engineering', 'engineering — platform and diagnostics'],
  ['admin', 'admin — the staff directory itself, and break-glass'],
]

/**
 * The five break-glass reason codes, from `staff.BreakGlassReasons`.
 *
 * The registry, in the backend's own order. A code an operator cannot find here
 * is one the server would refuse, so the list is copied rather than invented
 * and its order is left alone — a re-sorted registry is one where somebody's
 * muscle memory picks the wrong code under pressure.
 */
const ELEVATION_REASONS: readonly DecideReason[] = [
  ['incident_response', 'A live incident is being worked', ''],
  ['production_defect', 'A defect in production needs diagnosis', ''],
  ['security_investigation', 'A security question is being investigated', ''],
  ['regulatory_deadline', 'A regulator’s deadline cannot otherwise be met', ''],
  ['customer_harm', 'A customer is being harmed right now', ''],
]

const GRANT_REASONS: readonly DecideReason[] = [
  ['role_change', 'Their job changed', ''],
  ['new_joiner', 'They have just joined', ''],
  ['cover_for_absence', 'Covering somebody who is away', ''],
  ['access_review_finding', 'An access review said they should hold it', ''],
  ['incident_staffing', 'Staffing an incident', ''],
]

const REVOKE_REASONS: readonly DecideReason[] = [
  ['role_change', 'Their job changed', ''],
  ['leaver', 'They are leaving or have left', ''],
  ['cover_ended', 'The absence they were covering has ended', ''],
  ['access_review_finding', 'An access review said they should not hold it', ''],
  ['least_privilege', 'They no longer need it', ''],
]

const ATTEST_REASONS: readonly DecideReason[] = [
  ['periodic_review', 'The scheduled review came round', ''],
  ['role_change_review', 'Reviewed because their role changed', ''],
  ['audit_request', 'Reviewed at an auditor’s request', ''],
]

const ELEVATION_GRANTS_NOTHING =
  'AN ELEVATION GRANTS NOTHING. It is a record, not a permission: nothing checks it before a decision, and no screen or route becomes available because one is open. What it does is stamp its id on every audited action taken while it runs, so a reviewer can ask "what happened during this" as one query. If you cannot do something now, an elevation will not change that — the answer is a role, from an admin.'

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/**
 * Granting a role.
 *
 * The subject is typed rather than chosen. The directory is on screen, but it
 * is paginated in principle and an Ory subject is the identifier every other
 * surface uses — and unlike a price-list code, a wrong one fails loudly with a
 * 404 rather than quietly doing the right thing to the wrong person.
 */
export function grantRoleForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_ROLE_WRITE, ROLE_ADMIN, 'Granting a role')
  return {
    t: 'form',
    title: 'Grant a role',
    lead: 'Adds a role to somebody’s grant set. Roles nest — compliance reaches everything ops and support hold — so granting one usually grants more than its name suggests, and the confirmation shows the whole set before and after.',
    fields: [
      {
        name: 'subject', label: 'Ory subject', kind: 'text', required: true, mono: true,
        note: 'The identity id from the staff directory above, not an email address.',
      },
      {
        name: 'role', label: 'Role', kind: 'select', required: true, options: ROLES,
        note: 'The closed set. A role outside it is refused by the backend with the list named.',
      },
    ],
    reasons: GRANT_REASONS,
    reasonNote: 'Recorded on the grant and in the audit row, and read back at the next access review.',
    auditLine: 'admin.staff.role.grant · actor · staff:<subject> · reason',
    cta: 'Grant role',
    ctaNote: 'Takes effect on their next request. Nothing here signs them out or in.',
    blocked,
    ...(blocked === '' ? { submit: roleSubmit(api, 'grant') } : {}),
  }
}

/** Taking one back. Same shape, opposite direction, its own reason list. */
export function revokeRoleForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_ROLE_WRITE, ROLE_ADMIN, 'Revoking a role')
  return {
    t: 'form',
    title: 'Revoke a role',
    lead: 'Removes one role from somebody’s grant set. It does not remove what another role of theirs still reaches: somebody holding compliance keeps everything ops holds after ops is revoked, and the confirmation shows what they are left with.',
    fields: [
      { name: 'subject', label: 'Ory subject', kind: 'text', required: true, mono: true },
      { name: 'role', label: 'Role to remove', kind: 'select', required: true, options: ROLES },
    ],
    reasons: REVOKE_REASONS,
    reasonNote: 'Recorded on the revocation and in the audit row.',
    auditLine: 'admin.staff.role.revoke · actor · staff:<subject> · reason',
    cta: 'Revoke role',
    ctaNote: 'The grant history stays readable. Revoking does not erase that the role was held.',
    blocked,
    ...(blocked === '' ? { submit: roleSubmit(api, 'revoke') } : {}),
  }
}

/**
 * Both role writes, and the panel that shows the transition.
 *
 * `roles_before` and `roles_after` are both on the access row, because "the
 * difference between the roles snapshotted then and the roles held now is the
 * finding an access review exists to produce". The person who just made the
 * change is the one best placed to notice it was not what they meant, so the
 * panel shows the whole resulting set rather than the role they named.
 */
function roleSubmit(api: ApiClient, verb: 'grant' | 'revoke') {
  return (v: Readonly<Record<string, string>>) => {
    const subject = v['subject'] ?? ''
    const role = v['role'] ?? ''
    return submitWrite(
      () => api.post<unknown>(
        verb === 'grant' ? staffRolesPath(subject) : staffRoleRevokePath(subject, role),
        verb === 'grant'
          ? { role, reason: v['reason'] ?? '' }
          : { reason: v['reason'] ?? '' },
      ),
      verb === 'grant' ? 'The role was granted' : 'The role was revoked',
      (answer) => {
        const roles = stringsAt(answer, 'roles')
        const lines: FormLine[] = [
          ['staff', stringAt(answer, 'subject') || subject, ''],
          ['role', stringAt(answer, 'role') || role, verb === 'grant' ? 'added' : 'removed'],
        ]
        lines.push([
          'roles now',
          roles.length === 0 ? '— none' : roles.join(', '),
          'the whole set, not just what changed — roles nest',
        ])
        const grantID = numberAt(answer, 'grant_id')
        if (grantID !== null) lines.push(['grant', String(grantID), 'quote this at a review'])
        return lines
      },
    )
  }
}

// ---------------------------------------------------------------------------
// Access reviews
// ---------------------------------------------------------------------------

/**
 * Attesting at a periodic review.
 *
 * # There is no revoke here, and the absence is the interface's assertion
 *
 * `AccessReviewStore` has no revoke method: *"overdue is a state this surface
 * returns and never an action it takes."* A reviewer who finds something wrong
 * records `changes_requested` and the change is made by somebody holding the
 * role-write permission — which may be them, above, as a separate act with its
 * own audit row. Folding the two together would make an attestation and a
 * revocation indistinguishable in the trail.
 *
 * # The outcome has no default
 *
 * *"an attestation whose verdict was assumed is not an attestation."* Two
 * options, neither preselected.
 */
export function attestForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_ATTEST, 'Admin', 'Attesting at an access review')
  return {
    t: 'form',
    title: 'Attest an access review',
    lead: 'Records that somebody’s access was reviewed, and what the reviewer concluded. It changes nobody’s roles: an attestation is a statement about what was found, and acting on it is a separate grant or revocation with its own record.',
    fields: [
      { name: 'subject', label: 'Ory subject', kind: 'text', required: true, mono: true },
      {
        name: 'outcome', label: 'What the review found', kind: 'select', required: true,
        options: [
          ['confirmed', 'confirmed — the access they hold is right'],
          ['changes_requested', 'changes_requested — something should change'],
        ],
        note: 'No default, deliberately: an attestation whose verdict was assumed is not an attestation. Choosing changes_requested records the finding — it does not make the change.',
      },
    ],
    reasons: ATTEST_REASONS,
    reasonNote: 'Recorded on the attestation. This is what somebody reads at the next review to see what was considered.',
    auditLine: 'admin.staff.access_review.attest · actor · staff:<subject> · reason',
    cta: 'Record the attestation',
    ctaNote: 'Sets the next due date. Nothing on this screen removes access — that is a revocation, above.',
    blocked,
    ...(blocked === '' ? { submit: attestSubmit(api) } : {}),
  }
}

function attestSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(staffAttestPath(v['subject'] ?? ''), {
      outcome: v['outcome'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The attestation was recorded',
    (answer) => {
      const lines: FormLine[] = [
        ['staff', stringAt(answer, 'subject') || (v['subject'] ?? '—'), ''],
        ['found', stringAt(answer, 'outcome') || (v['outcome'] ?? '—'), ''],
      ]
      const roles = stringsAt(answer, 'roles_attested')
      if (roles.length > 0) {
        lines.push(['roles attested', roles.join(', '), 'what they held at this moment'])
      }
      const due = stringAt(answer, 'next_due_at')
      if (due !== '') lines.push(['next due', due, ''])
      if ((v['outcome'] ?? '') === 'changes_requested') {
        lines.push(['still to do', 'nothing changed yet — make the grant or revocation separately', ''])
      }
      return lines
    },
  )
}

// ---------------------------------------------------------------------------
// Break-glass
// ---------------------------------------------------------------------------

/** One elevation, as far as the acknowledge and review controls need to know. */
export interface ElevationStanding {
  readonly id: number
  readonly subject: string
  readonly state: string
  readonly requestedBy: string
  readonly awaitingReview: boolean
  /** Audited actions taken under it, or `null` before it opened. */
  readonly actions: number | null
}

/**
 * Requesting an elevation.
 *
 * Leads with what it does not do, because that is the sentence somebody needs
 * before they spend two minutes of an incident on this rather than on asking an
 * admin for a role.
 */
export function requestElevationForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_BREAK_GLASS, ROLE_ADMIN, 'Requesting a break-glass elevation')
  return {
    t: 'form',
    title: 'Request a break-glass elevation',
    lead: ELEVATION_GRANTS_NOTHING,
    fields: [
      {
        name: 'subject', label: 'Who is being elevated', kind: 'text', required: true, mono: true,
        // Not prefilled with the requester: raising one on somebody else's
        // behalf during an incident is ordinary, and a default would make the
        // row say it was raised on the requester's.
        note: 'Usually you, and deliberately not filled in for you — raising one on a colleague’s behalf during an incident is ordinary, and a default would put the wrong name on the record.',
      },
      {
        name: 'time_box_minutes', label: 'Time box', kind: 'select', required: true,
        options: [['15', '15 minutes'], ['30', '30 minutes'], ['60', '60 minutes']],
        note: 'Three values, and no others exist. A free duration is what turns an emergency into a standing privilege.',
      },
      {
        name: 'reason_code', label: 'Why', kind: 'select', required: true,
        options: ELEVATION_REASONS.map(([c, g]) => [c, `${c} — ${g}`] as const),
        note: 'From the managed registry. This is what a quarterly review groups by; the sentence below is what it reads.',
      },
    ],
    reasons: ELEVATION_REASONS,
    reasonNote: 'What is happening, in a sentence somebody will read weeks later with none of today’s context.',
    auditLine: 'admin.break_glass.request · actor · break_glass:<id> · reason',
    cta: 'Request elevation',
    ctaNote: 'This creates a REQUEST, not a session. Nothing opens until a second admin acknowledges it — and even then nothing new becomes possible.',
    blocked,
    ...(blocked === '' ? { submit: requestSubmit(api) } : {}),
  }
}

function requestSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(BREAK_GLASS_WRITE_PATH, {
      subject: v['subject'] ?? '',
      reason_code: v['reason_code'] ?? '',
      time_box_minutes: Number(v['time_box_minutes'] ?? ''),
      reason: v['reason'] ?? '',
    }),
    'The elevation was requested',
    (answer) => {
      const id = numberAt(answer, 'id')
      const state = stringAt(answer, 'state')
      return [
        ...(id === null ? [] : [['elevation', String(id), 'quote this to whoever acknowledges'] as const]),
        ['state', state === '' ? 'pending_acknowledgement' : state, 'a request, not a session'],
        ['what you can do now', 'exactly what you could do before', 'an elevation records; it does not grant'],
      ]
    },
  )
}

/**
 * The second admin opening it.
 *
 * `ErrSelfAcknowledge` is a 403 with `maker_is_checker`. Stated here before the
 * click so the requester is not told at the end of composing a reason.
 */
export function acknowledgeElevationForm(
  api: ApiClient, caller: Caller, e: ElevationStanding,
): FormBlock {
  const session = writeRefusal(caller, PERM_BREAK_GLASS, ROLE_ADMIN, 'Acknowledging an elevation')
  const state = e.state !== 'pending_acknowledgement'
    ? `Disabled — this elevation is ${e.state === '' ? 'in a state this read did not establish' : e.state}, not awaiting acknowledgement. Only a pending request can be opened.`
    : ''
  const blocked = session !== ''
    ? session
    : state !== ''
      ? state
      : secondPersonRefusal(caller, e.requestedBy, 'requested this elevation', 'Acknowledging it', actorsAreIdentical)

  return {
    t: 'form',
    title: `Acknowledge elevation ${String(e.id)}`,
    lead: `Opens the time box on ${e.subject === '' ? 'this request' : e.subject}’s request, and starts the clock. It still grants nothing — what changes is that audited actions from now until it expires carry this elevation’s id, so a reviewer can read them as one set.`,
    fields: [],
    reasons: ACKNOWLEDGE_REASONS,
    reasonNote: 'Why you agree this is warranted. Read at the post-hoc review alongside what was actually done.',
    auditLine: `admin.break_glass.acknowledge · actor · break_glass:${String(e.id)} · reason`,
    cta: 'Acknowledge and start the clock',
    ctaNote: 'The time box runs from now, not from when it was requested.',
    blocked,
    ...(blocked === '' ? { submit: elevationSubmit(api, e, 'acknowledge') } : {}),
  }
}

const ACKNOWLEDGE_REASONS: readonly DecideReason[] = [
  ['incident_confirmed', 'The incident is real and I have seen it', ''],
  ['request_proportionate', 'What is being asked for fits the problem', ''],
  ['escalation_agreed', 'Agreed on the escalation call', ''],
]

const REVIEW_REASONS: readonly DecideReason[] = [
  ['post_incident_review', 'Read back as part of the incident review', ''],
  ['quarterly_review', 'Read back at the scheduled review', ''],
  ['spot_check', 'Read back as a spot check', ''],
]

/**
 * Reading an expired elevation back.
 *
 * `ErrSelfReview` bars the requester, and it is enforced in the handler AND in
 * a schema CHECK — *"the schema is what survives a refactor, and the handler is
 * what produces a sentence an operator can act on rather than a constraint
 * name."* This is the third place that rule is stated, and the only one an
 * operator reads before acting.
 *
 * The finding is a closed pair *"so a quarterly count of questioned elevations
 * is a query rather than a reading exercise."*
 */
export function reviewElevationForm(
  api: ApiClient, caller: Caller, e: ElevationStanding,
): FormBlock {
  const session = writeRefusal(caller, PERM_BREAK_GLASS, ROLE_ADMIN, 'Reviewing an elevation')
  const state = !e.awaitingReview
    ? 'Disabled — this elevation is not awaiting review. A review reads back an elevation whose window has closed, and one that is still running has nothing settled to read.'
    : ''
  const blocked = session !== ''
    ? session
    : state !== ''
      ? state
      : secondPersonRefusal(caller, e.requestedBy, 'requested this elevation', 'Reviewing it', actorsAreIdentical)

  return {
    t: 'form',
    title: `Review elevation ${String(e.id)}`,
    lead: reviewLead(e),
    fields: [
      {
        name: 'finding', label: 'Finding', kind: 'select', required: true,
        options: [
          ['appropriate', 'appropriate — the elevation and what was done under it were warranted'],
          ['questioned', 'questioned — something here needs following up'],
        ],
        note: 'A closed pair, so a count of questioned elevations is a query rather than a reading exercise. No default.',
      },
    ],
    reasons: REVIEW_REASONS,
    reasonNote: 'What you looked at and what you concluded.',
    auditLine: `admin.break_glass.review · actor · break_glass:${String(e.id)} · reason`,
    cta: 'Record the finding',
    ctaNote: 'The elevation stays on the record either way. A questioned finding is a note on it, not a reversal — there is nothing to reverse, because it granted nothing.',
    blocked,
    ...(blocked === '' ? { submit: elevationSubmit(api, e, 'review') } : {}),
  }
}

/**
 * What the reviewer is looking at, including the zero.
 *
 * `actions` is present once a session opens, including when it is zero,
 * because *"'they elevated and did nothing' is a review finding, and hiding the
 * zero would make it indistinguishable from a session nobody counted."*
 */
function reviewLead(e: ElevationStanding): string {
  const who = e.requestedBy === '' ? 'somebody this read did not name' : e.requestedBy
  if (e.actions === null) {
    return `Reads back the closed elevation on ${e.subject === '' ? 'this subject' : e.subject}, requested by ${who}. This read did not say how many audited actions carried its id, so that is a thing to establish before concluding anything.`
  }
  if (e.actions === 0) {
    return `Reads back the closed elevation on ${e.subject === '' ? 'this subject' : e.subject}, requested by ${who}. NO audited action carried its id — the elevation was opened and nothing was done under it. That is a finding in its own right, not an empty screen.`
  }
  return `Reads back the closed elevation on ${e.subject === '' ? 'this subject' : e.subject}, requested by ${who}. ${String(e.actions)} audited action(s) carried its id, and they are what this review is about.`
}

function elevationSubmit(
  api: ApiClient, e: ElevationStanding, verb: 'acknowledge' | 'review',
) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(
      elevationPath(e.id, verb),
      verb === 'review'
        ? { finding: v['finding'] ?? '', reason: v['reason'] ?? '' }
        : { reason: v['reason'] ?? '' },
    ),
    verb === 'acknowledge' ? 'The elevation is open' : 'The review is recorded',
    (answer) => {
      const lines: FormLine[] = [['elevation', String(e.id), '']]
      const state = stringAt(answer, 'state')
      if (state !== '') lines.push(['state', state, ''])
      if (verb === 'acknowledge') {
        const expires = stringAt(answer, 'expires_at')
        if (expires !== '') lines.push(['expires', expires, 'the clock runs from now'])
        lines.push([
          'what changed', 'nothing anybody can do',
          'actions taken until it expires carry this id, and that is all',
        ])
      } else {
        lines.push(['finding', stringAt(answer, 'review_finding') || (v['finding'] ?? '—'), ''])
      }
      return lines
    },
  )
}

// ---------------------------------------------------------------------------

function stringAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const v = (o as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : ''
}

/**
 * One number off an answer, or nothing.
 *
 * `exactInteger` (#207). This reads the elevation id back off a 201 and the
 * console shows it to the operator to quote; an id rounded by `JSON.parse` is a
 * different elevation, and the number would look entirely ordinary.
 */
function numberAt(o: unknown, key: string): number | null {
  if (typeof o !== 'object' || o === null) return null
  return exactInteger((o as Record<string, unknown>)[key])
}

function stringsAt(o: unknown, key: string): readonly string[] {
  if (typeof o !== 'object' || o === null) return []
  const v = (o as Record<string, unknown>)[key]
  if (!Array.isArray(v)) return []
  return v.filter((s): s is string => typeof s === 'string')
}
